import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import FirstPartReasonField from './FirstPartReasonField';

// 'YYYY-MM-DD' -> 'DD.MM.YYYY.'
const fmtDeadline = (s) => {
  const [y, m, d] = String(s || '').split('-');
  return d ? `${d}.${m}.${y}.` : '—';
};

function renderCapacitySection(t, item, key) {
  const {
    requestedDays,
    earnedAvailable,
    hardRemaining,
    softDeficit,
    hardDeficit,
    meta = {},
  } = item || {};

  return (
    <div key={key} style={{ marginBottom: 12 }}>
      <p style={{ marginBottom: 8 }}>
        {t('requestedVsAvailable', {
          ns: 'messages',
          defaultValue:
            'Requested: {{r}} day(s) · Currently accrued: {{a}} day(s)',
          r: requestedDays ?? '—',
          a: earnedAvailable ?? '—',
        })}
      </p>
      <ul style={{ paddingLeft: 16, margin: 0 }}>
        <li>
          {t('hardRemaining', {
            ns: 'messages',
            defaultValue: 'Hard remaining (total entitlement left): {{x}}',
            x: hardRemaining ?? '—',
          })}
        </li>
        <li>
          {t('softDeficit', {
            ns: 'messages',
            defaultValue: 'Soft deficit (accrual shortfall): {{x}}',
            x: softDeficit ?? '—',
          })}
        </li>
        <li>
          {t('hardDeficit', {
            ns: 'messages',
            defaultValue: 'Hard deficit (over total entitlement): {{x}}',
            x: hardDeficit ?? '—',
          })}
        </li>
        {meta?.limitedEmployment ? (
          <li>
            {t('limitedContractYear', {
              ns: 'messages',
              defaultValue: 'Limited employment · Holiday year: {{y}}',
              y: meta.holidayDaysYear ?? '—',
            })}
          </li>
        ) : null}
      </ul>
    </div>
  );
}

function renderFirstBlockSection(t, item, key) {
  const { newDaysInThisRequest, minRequired = 10 } = item || {};
  const usedOldDays =
    typeof item?.requestedDays === 'number' &&
    typeof newDaysInThisRequest === 'number'
      ? Math.max(item.requestedDays - newDaysInThisRequest, 0)
      : 0;
  const suggested = usedOldDays + minRequired;

  return (
    <div
      key={key}
      style={{
        marginBottom: 12,
        padding: 12,
        border: '1px solid #f0ad4e',
        background: '#fff7e6',
        borderRadius: 4,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        {t('firstBlockSectionTitle', {
          ns: 'messages',
          defaultValue: 'Labour Law compliance notice (Art. 73)',
        })}
      </div>
      <p style={{ margin: 0 }}>
        {t('firstBlockViolation', {
          ns: 'messages',
          defaultValue:
            'The first part of annual leave should be an uninterrupted block of at least {{minRequired}} working days. This request uses {{newDays}} new-year day(s). Recommendation: at least {{suggested}} working day(s).',
          minRequired,
          newDays: newDaysInThisRequest ?? '—',
          suggested,
        })}
      </p>
    </div>
  );
}

function renderFirstPartSection(t, item, key) {
  return (
    <div
      key={key}
      style={{
        marginBottom: 12,
        padding: 12,
        border: '1px solid #f0ad4e',
        background: '#fff7e6',
        borderRadius: 4,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        {t('firstPartSectionTitle', { ns: 'messages' })}
      </div>
      <p style={{ margin: 0 }}>
        {t(item?.trigger === 'removed' ? 'firstPartRemoved' : 'firstPartShort', {
          ns: 'messages',
          required: item?.required ?? '—',
          deadline: fmtDeadline(item?.deadline),
          block: item?.blockDays ?? 0,
          best: item?.bestBlockDays ?? 0,
        })}
      </p>
      {item?.pendingDays > 0 ? (
        <p style={{ margin: '6px 0 0' }}>
          {t('firstPartPending', { ns: 'messages', days: item.pendingDays })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Soft-warning dialog for leave requests (capacity, Art. 73 first block, first
 * part by 31.12.). With a first-part warning the OK button needs a reason,
 * passed to `onOk` as `{ reason }` — send it as `lawWarningReason`.
 *
 * @param {Object} p
 * @param {Function} p.t
 * @param {Object} p.response - Server warning response ({ message, details }).
 * @param {Function} p.onOk - Called with `{ reason }` when confirmed.
 * @param {'submit'|'approve'|'delete'} [p.action='submit'] - Wording of the OK button.
 */
export function showCapacityWarningModal({ t, response, onOk, action = 'submit' }) {
  const { details = [], message } = response || {};

  const list = Array.isArray(details) ? details : [details];

  const fallbackTitle = t('holiday_leave_exceeds_currently_available', {
    ns: 'messages',
    defaultValue: 'Requested leave exceeds currently accrued days',
  });

  // The server sends the capacity message for every warning, so a request that
  // only breaks the Art. 73 first-block rule was titled as exceeding accrued
  // days while the body said something else.
  const onlyFirstBlock =
    list.length > 0 && list.every((item) => item?.kind === 'firstBlock');
  const hasFirstPart = list.some((item) => item?.kind === 'firstPart');
  const onlyFirstPart =
    hasFirstPart && list.every((item) => item?.kind === 'firstPart');
  const reasonHolder = { reason: '', setError: null };

  const title = onlyFirstPart
    ? t('holiday_leave_first_part_title', { ns: 'messages' })
    : onlyFirstBlock
    ? t('holiday_leave_first_block_title', { ns: 'messages' })
    : message
      ? t(message, {
          ns: message.startsWith('request.') ? 'translations' : 'messages',
          defaultValue: fallbackTitle,
        })
      : fallbackTitle;

  Modal.confirm({
    icon: <ExclamationCircleOutlined />,
    width: 640,
    title,
    content: (
      <div style={{ lineHeight: 1.6 }}>
        {list.map((item, idx) => {
          if (!item) return null;
          if (item.kind === 'firstBlock') {
            return renderFirstBlockSection(t, item, idx);
          }
          if (item.kind === 'firstPart') {
            return renderFirstPartSection(t, item, idx);
          }
          if (item.kind === 'capacity') {
            return renderCapacitySection(t, item, idx);
          }
          return null;
        })}
        <p style={{ marginTop: 12 }}>
          {hasFirstPart
            ? t('firstPartProceedInfo', { ns: 'messages' })
            : t('proceedAnywayInfo', {
                ns: 'messages',
                defaultValue:
                  'You can proceed anyway and submit this request now. On OK we will continue and save it.',
              })}
        </p>
        {hasFirstPart ? (
          <FirstPartReasonField t={t} holder={reasonHolder} />
        ) : null}
      </div>
    ),
    okText: hasFirstPart
      ? t(
          { approve: 'approveAnyway', delete: 'deleteAnyway' }[action] ||
            'submitAnyway',
          { ns: 'forms' }
        )
      : t('proceedAnyway', { ns: 'forms', defaultValue: 'Proceed anyway' }),
    cancelText: t('cancel', { ns: 'forms', defaultValue: 'Cancel' }),
    maskClosable: false,
    // Declaring the `close` argument keeps the dialog open while the reason is
    // missing; a returned promise closes it once settled successfully.
    // eslint-disable-next-line no-unused-vars
    onOk: (close) => {
      const reason = reasonHolder.reason.trim();
      if (hasFirstPart && !reason) {
        reasonHolder.setError?.(true);
        return undefined;
      }
      return Promise.resolve(onOk?.({ reason }));
    },
  });
}
